import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import { loadCommonFinalizingHireSteps } from '../../action/job/load-common-finalizing-hire-steps';
import { loadJobDetails } from '../../action/job/load-job-details';
import { loadJob } from '../../job/actions/load-job';
import {
  commonFinalizingHireStepsSelector,
  jobDetailsSelector,
  jobSelector,
} from '../../reducers/job-selectors';

import { JobFieldFinalizingHireSteps } from '../../job/fields/job-field-finalizing-hire-steps';
import { JobFieldFinalizingHireUpdateSetting } from '../../job/fields/job-field-finalizing-hire-update-setting';

import { JobFlowButtons } from './job-flow-buttons';

import { LoadingMessage } from '../../widgets/loading-message';

const FORM_NAME = 'jobFlowFinalizingHireSteps';

const mapStateToProps = (state: any, { jobId }: InputProps) => {
  const jobDetails = (jobId && jobDetailsSelector(state, jobId)) || ({} as JobDetails);
  const job = (jobId && jobSelector(state, jobId)) || ({} as Job);

  return {
    jobDetails,
    // initialValues needed for redux form
    initialValues: {
      JobFinalizingHireSteps: jobDetails.JobFinalizingHireSteps,
      finalizingHireStepUpdateSetting: job.finalizingHireStepUpdateSetting,
    },
    commonFinalizingHireSteps: commonFinalizingHireStepsSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { jobId }: InputProps,
) => ({
  actions: {
    loadJob: () => (jobId ? dispatch(loadJob(jobId)) : null),
    loadJobDetails: () => (jobId ? dispatch(loadJobDetails(jobId)) : null),
    loadCommonFinalizingHireSteps: () => dispatch(loadCommonFinalizingHireSteps()),
  },
});

interface InputProps extends JobFlowStep {
  jobId?: number;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type JobFlowPreHireStepsComponentProps = InputProps & StateProps & DispatchProps;
type JobFlowPreHireStepsFields = Partial<Job>;
type Props = InjectedFormProps<JobFlowPreHireStepsFields, JobFlowPreHireStepsComponentProps> &
  JobFlowPreHireStepsComponentProps;

const JobFlowPreHireStepsComponent = ({
  actions,
  jobDetails,
  commonFinalizingHireSteps,
  dirty,
  onDirty,
  onNext,
  onSkip,
  onPrev,
  editing,
  saving,
  handleSubmit,
}: Props) => {
  useEffect(() => {
    actions.loadJob();
    actions.loadJobDetails();
    actions.loadCommonFinalizingHireSteps();
  });

  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);

  const submit = (job: JobFlowPreHireStepsFields) => {
    onNext(job);
  };

  if (!commonFinalizingHireSteps || !jobDetails || !jobDetails.JobFinalizingHireSteps) {
    return <LoadingMessage />;
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <JobFieldFinalizingHireSteps commonFinalizingHireSteps={commonFinalizingHireSteps} />
      <JobFieldFinalizingHireUpdateSetting />
      <JobFlowButtons onSkip={onSkip} onPrev={onPrev} editing={editing} saving={saving} />
    </form>
  );
};

export const JobFlowPreHireSteps = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<JobFlowPreHireStepsFields, JobFlowPreHireStepsComponentProps>({
    form: FORM_NAME,
    enableReinitialize: true,
  })(JobFlowPreHireStepsComponent),
);
